import { useEffect, useState } from 'react';

const VERSION_CHECK_INTERVAL_MS = 5 * 60_000;

interface VersionResponse {
  version: string;
}

async function fetchLatestVersion(): Promise<string | null> {
  try {
    const response = await fetch(`/version.json?ts=${String(Date.now())}`, { cache: 'no-store' });
    if (!response.ok) return null;
    const data: unknown = await response.json();
    if (
      typeof data !== 'object' ||
      data === null ||
      typeof (data as VersionResponse).version !== 'string'
    ) {
      return null;
    }
    return (data as VersionResponse).version || null;
  } catch {
    return null;
  }
}

/**
 * 새 배포가 나가면(=`/version.json`의 값이 현재 실행 중인 `__APP_VERSION__`과 달라지면) true를 반환한다.
 * CSR 특성상 이미 열려있는 탭은 새 배포를 스스로 알 수 없으므로, 주기적 폴링 + 탭 포그라운드 복귀 시점에 재확인한다.
 */
export default function useVersionCheck(): boolean {
  const [hasNewVersion, setHasNewVersion] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkVersion = async () => {
      const latestVersion = await fetchLatestVersion();
      if (!cancelled && latestVersion && latestVersion !== __APP_VERSION__) {
        setHasNewVersion(true);
      }
    };

    void checkVersion();
    const intervalId = setInterval(() => void checkVersion(), VERSION_CHECK_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void checkVersion();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return hasNewVersion;
}
